import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { GET_PTO_USERS } from '../../graphql/team-availability-queries';
import { API_URL } from '../../config';
import PtoHistoryPanel from './PtoHistoryPanel';

const ITEMS_PER_PAGE = 20;

const ROLE_OPTIONS = [
  { value: 'ALL', label: 'All roles' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'GUEST_APP_MANAGER', label: 'Guest App Manager' },
  { value: 'PROPERTY_MANAGER', label: 'Property Manager' },
  { value: 'HOUSEKEEPER_MANAGER', label: 'Housekeeper Manager' },
  { value: 'INSPECTOR', label: 'Inspector' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
  { value: 'CALL_CENTER', label: 'Concierge / Call Center' },
  { value: 'HOUSEKEEPER', label: 'Housekeeper' },
  { value: 'USER', label: 'User' },
];

const formatRole = (role) =>
  ROLE_OPTIONS.find((option) => option.value === role)?.label
  || role?.replace(/_/g, ' ')
  || 'Role not set';

function PaidTimeOffSettingsView() {
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [draftDays, setDraftDays] = useState({});
  const [savingUserId, setSavingUserId] = useState(null);
  const [historyUser, setHistoryUser] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  const { data, previousData, loading, error, refetch } = useQuery(GET_PTO_USERS, {
    variables: {
      page: currentPage,
      limit: ITEMS_PER_PAGE,
      search: searchTerm.trim() || null,
      role: roleFilter === 'ALL' ? null : roleFilter,
    },
    fetchPolicy: 'network-only',
  });

  const pageData = data?.paginatedUsers || previousData?.paginatedUsers;
  const users = useMemo(() => pageData?.items || [], [pageData?.items]);
  const total = pageData?.total || 0;
  const initialLoading = loading && !pageData;

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [roleFilter, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));
  const pageStart = (currentPage - 1) * ITEMS_PER_PAGE;

  const getDraftValue = (user) => {
    if (Object.prototype.hasOwnProperty.call(draftDays, user.id)) return draftDays[user.id];
    return String(user.paidTimeOffDays ?? 0);
  };

  const handleDaysChange = (userId, value) => {
    if (value !== '' && !/^\d{0,3}$/.test(value)) return;
    setDraftDays((previous) => ({ ...previous, [userId]: value }));
  };

  const handleSave = async (user) => {
    const rawValue = getDraftValue(user);
    const paidTimeOffDays = Number(rawValue);
    if (!Number.isInteger(paidTimeOffDays) || paidTimeOffDays < 0 || paidTimeOffDays > 365) {
      toast.error('PTO days must be a whole number between 0 and 365');
      return;
    }

    try {
      setSavingUserId(user.id);
      const token = localStorage.getItem('access_token');
      await axios.patch(
        `${API_URL}/api/auth/profile/${user.id}`,
        { paidTimeOffDays },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );
      toast.success(`PTO allowance updated for ${user.name}`);
      setDraftDays((previous) => {
        const next = { ...previous };
        delete next[user.id];
        return next;
      });
      await refetch();
    } catch (saveError) {
      toast.error(saveError.response?.data?.message || saveError.message || 'Failed to update PTO allowance');
    } finally {
      setSavingUserId(null);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-4 sm:p-6 border-b border-gray-200">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Paid Time Off</h2>
            <p className="mt-1 text-sm text-gray-600">
              Set each active team member's annual PTO allowance. Select a team member's name to audit yearly usage and the exact dates included in the balance.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <div>
              <label htmlFor="pto-role-filter" className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <select
                id="pto-role-filter"
                value={roleFilter}
                onChange={(event) => setRoleFilter(event.target.value)}
                className="w-full sm:w-56 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
              >
                {ROLE_OPTIONS.map((role) => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="pto-search" className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <input
                id="pto-search"
                type="search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search team members..."
                className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
              />
              {loading && pageData && (
                <span className="mt-1 block text-xs text-gray-500">Searching...</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {initialLoading ? (
        <div className="p-8 text-center text-gray-500">Loading...</div>
      ) : error ? (
        <div className="p-8 text-center text-red-600">Error: {error.message}</div>
      ) : users.length === 0 ? (
        <div className="p-8 text-center text-gray-500">No active team members found.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Team Member</th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Annual PTO Days</th>
                <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map((user) => {
                const value = getDraftValue(user);
                const currentValue = String(user.paidTimeOffDays ?? 0);
                const isDirty = value !== currentValue;
                return (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-4 sm:px-6 py-4">
                      <button
                        type="button"
                        onClick={() => {
                          setHistoryUser(user);
                          setHistoryOpen(true);
                        }}
                        className="group inline-flex items-center gap-1.5 text-left text-sm font-semibold text-sky-700 hover:text-sky-900 hover:underline focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 rounded"
                        aria-label={`View PTO history for ${user.name}`}
                      >
                        {user.name}
                        <svg className="h-4 w-4 transition-transform group-hover:translate-x-0.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 010-1.06L10.92 10 7.21 6.29a.75.75 0 011.06-1.06l4.24 4.24a.75.75 0 010 1.06l-4.24 4.24a.75.75 0 01-1.06 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      <div className="text-xs text-gray-500">{user.email}</div>
                    </td>
                    <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-600">{formatRole(user.role)}</td>
                    <td className="px-4 sm:px-6 py-4">
                      <input
                        type="number"
                        min="0"
                        max="365"
                        step="1"
                        aria-label={`Annual PTO days for ${user.name}`}
                        value={value}
                        onChange={(event) => handleDaysChange(user.id, event.target.value)}
                        className="w-28 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                      />
                    </td>
                    <td className="px-4 sm:px-6 py-4 text-right">
                      <button
                        type="button"
                        onClick={() => handleSave(user)}
                        disabled={!isDirty || savingUserId === user.id}
                        className="px-4 py-2 rounded-md text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {savingUserId === user.id ? 'Saving...' : 'Save'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {total > 0 && (
        <div className="p-4 sm:px-6 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <span className="text-sm text-gray-600">
            Showing {pageStart + 1}–{Math.min(pageStart + users.length, total)} of {total} team members
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 rounded-md text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">Page {currentPage} of {totalPages}</span>
            <button
              type="button"
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 rounded-md text-sm font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      <PtoHistoryPanel
        isOpen={historyOpen}
        user={historyUser}
        onClose={() => setHistoryOpen(false)}
        onChange={refetch}
      />
    </div>
  );
}

export default PaidTimeOffSettingsView;
